import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useState } from 'react';

import { VpFancyButton } from '@vp/common/ui/component/VpFancyButton';
import { VpImage } from '@vp/common/ui/component/VpImage';
import { VpMediaGradientOverlay } from '@vp/common/ui/component/VpMediaGradientOverlay';
import { ProfilePhotoModel } from '@vp/profile/core/model/ProfilePhotoModel';

import { CommonSlideshowBlurhash } from './SlideshowContent';

interface SlideshowIntroContentProps {
  name: string;
  image: ProfilePhotoModel;
  imageScale: number;
  buttonText: string;
  birthDeathDates: string;
  startSlideshow: () => void;
  skipSlideshow: () => void;
}

const ButtonGradient =
  'linear-gradient(90.82deg, rgba(229, 229, 229, 0.35) -43.91%, rgba(229, 229, 229, 0.15) 48.8%, rgba(229, 229, 229, 0) 150.27%)';

const ImageOverlayGradient = 'linear-gradient(180deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.36) 54.57%, rgba(0, 0, 0, 0.18) 100%)';

export const SlideshowIntro: FC<SlideshowIntroContentProps> = ({
  startSlideshow,
  skipSlideshow,
  image,
  buttonText,
  birthDeathDates,
  name,
}) => {
  const [lockInteractions, setLockInteractions] = useState(false);

  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.only('mobile'));
  const imageMode = isMobile ? 'cover' : 'contain';

  const handleButtonClick = useCallback(
    (callback: () => void) => {
      if (!lockInteractions) {
        setLockInteractions(true);
        callback();
      }
    },
    [lockInteractions],
  );

  const onStartSlideshow = useCallback(() => handleButtonClick(startSlideshow), [handleButtonClick, startSlideshow]);
  const onSkipSlideshow = useCallback(() => handleButtonClick(skipSlideshow), [handleButtonClick, skipSlideshow]);

  return (
    <Box sx={theme => ({ width: 1, height: 1, zIndex: theme.zIndex.slideshowIntro })}>
      <VpMediaGradientOverlay sx={{ width: '100%', height: '100%', overflow: 'hidden' }} gradient={ImageOverlayGradient}>
        <VpImage src={image.staticUrl ?? image.url} hash={CommonSlideshowBlurhash} mode={imageMode} animateAppearance={false} />
      </VpMediaGradientOverlay>

      <Stack
        px={{ mobile: 5, tablet: 13, desktop: 21.25 }}
        sx={theme => ({
          inset: 0,
          position: 'absolute',
          justifyContent: 'space-between',
          zIndex: theme.zIndex.galleryProfileInfo,
          pt: { mobile: 9, tablet: 13, desktop: 21.25 },
          px: { mobile: 5, tablet: 13, desktop: 21.25 },
        })}
      >
        <Stack width={1} maxWidth={{ tablet: 300 }}>
          <Typography variant="h1" color="text.secondary">
            {name}
          </Typography>
          <Typography variant="body1Medium" color="text.secondary">
            {birthDeathDates}
          </Typography>
        </Stack>

        <Stack width={1} gap={1} pb={{ mobile: 3, tablet: 7 }} alignItems="center">
          <Button
            disableRipple
            sx={{ backgroundColor: 'transparent', textTransform: 'none', px: 2.5, py: 1.625 }}
            onClick={onSkipSlideshow}
          >
            <Typography variant="buttonLarge" color="primary.500">
              Перейти к профилю
            </Typography>
          </Button>

          <VpFancyButton text={buttonText} sx={{ background: ButtonGradient }} onClick={onStartSlideshow}></VpFancyButton>
        </Stack>
      </Stack>
    </Box>
  );
};
